/**
 * @file  src\pages\AgentCreateOrEditWantAdDraftListing.js
 * @brief  This file is responsible for agent want ad creation start page
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect } from "react";
import CommonHeader from "../../components/Onboarding/CommonHeader";
import SegmentDivider from "../../components/MemberWantAd/SegmentDivider";
import MyDrafts from "../../components/Common/MyDrafts";
import { useNavigate } from "react-router-dom";
import {
  AGENT_WANTAD_KEY,
  BUY,
  USER_INFO_KEY,
  USER_TYPE_AGENT,
} from "../../constants/common";
import {
  getMyDraft,
  getWantAdById,
  saveMemberWantAd,
  getProfileSlug,
} from "../../services/v2/wantAdService";
import Sidebar from "../../components/Onboarding/v2/Sidebar";
import { pageURLs } from "../../constants/pageURLs";
import {
  checkTutorialDisplayStatus,
  generateBase64AgentId,
  getLocalStorage,
  getUserType,
  loggedInUser,
} from "../../helpers/common";
import BuyerList from "../../images/buyer_listing_img.png";
import { useDispatch, useSelector } from "react-redux";
import PremiumFeatureIcon from "../../images/v2/premium-features-stars.svg";
import CustomLinkModal from "../../components/Common/v2/CustomLinkModal";
import PremiumPlanModal from "../../components/PlanUpgrade/PremiumPlanModal";
import { fetchUserDetails } from "../../actions/userprofile";

const AgentCreateOrEditWantAdDraftListing = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [myDrafts, setMyDrafts] = useState(null);
  const [userSlug, setUserSlug] = useState("");
  const userType = getUserType();
  const isAgent = userType === USER_TYPE_AGENT ? true : false;

  const sessionData = getLocalStorage(USER_INFO_KEY);
  const userId = _.get(sessionData, "userInfo.userId", "");

  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );

  const [isPremiumUser, setIsPremiumUser] = useState(null);
  const agent = loggedInUser();

  // Custom link management states
  const [linkToShare, setLinkToShare] = useState("");
  const [showCustomLinkPopuop, setShowCustomLinkPopup] = useState(false);

  // control handlers for custom link popup
  const handleCustomLinkPopupOpen = () => {
    setShowCustomLinkPopup(true);
  };

  const handleCustomLinkPopupClose = () => {
    setShowCustomLinkPopup(false);
  };

  //Plan modal for non-pro agent
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const [reload, setReload] = useState(0);

  const updateReloadParam = () => {
    setReload(reload + 1);
  };

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      if (userDetailsFromStore[0]?.period !== 1 && userType === 2) {
        setIsPremiumUser(true);
      } else {
        setIsPremiumUser(false);
      }
    }
  }, [userDetailsFromStore]);

  useEffect(() => {
    //Fetch user details again if membership plan is upgraded
    dispatch(fetchUserDetails());
  }, [reload]);

  useEffect(() => {
    //Need to show want add onboard very first time
    let DisplayWantAddOnboard = checkTutorialDisplayStatus(6); //Want add onboard display
    if (DisplayWantAddOnboard) {
      navigate(pageURLs.gitchaMemberWelcome);
    }
  }, []);

  useEffect(() => {
    localStorage.removeItem(AGENT_WANTAD_KEY);
    if (isPremiumUser != null) {
      getMyDraft()
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (responseResult && responseResult.length !== 0) {
            setMyDrafts(responseResult);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  }, [isPremiumUser]);
  /**
   * To handle navigation to edit want ad on clicking draft want ad
   * @param {*} requestPayLoad
   */
  const handleDraftItemClick = async (requestPayLoad) => {
    let wantAdId = requestPayLoad.selectedId;
    if (wantAdId) {
      await getWantAdById(wantAdId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            localStorage.setItem(
              AGENT_WANTAD_KEY,
              JSON.stringify(responseResult),
            );
            navigate(pageURLs.agentCreatewantad);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };
  /**
   * To load draft want ads
   * @returns
   */
  const loadDrafts = () => {
    if (myDrafts) {
      const myDraftList = myDrafts?.map((md) => {
        const wantAdId = _.get(md, "Want_ad_Id");
        const CompletedPercentage = _.get(md, "CompletedPercentage", "");
        const adTypeId = _.get(md, "Buy_or_Rent", "");
        const Current_Step = _.get(md, "Current_Step", "");
        const IsReadyToReview = _.get(md, "IsReadyToReview", "");
        const IsFilledByClient = _.get(md, "IsFilledByClient", "");
        let lastUpdatedDate = new Date(_.get(md, "LastUpdatedTS", ""));
        let formattedDate = [
          ("0" + (lastUpdatedDate.getMonth() + 1)).slice(-2),
          ("0" + lastUpdatedDate.getDate()).slice(-2),
          lastUpdatedDate.getFullYear(),
        ].join("/");
        let adType = "";
        if (adTypeId === 1) {
          adType = "Buy";
        } else if (adTypeId === 2) {
          adType = "Rent (Long-term)";
        } else if (adTypeId === 3) {
          adType = "Rent (Short-term)";
        }
        let clientName;
        if (_.get(md, "ClientFirstName") && _.get(md, "ClientLastName")) {
          clientName =
            _.get(md, "ClientFirstName") + " " + _.get(md, "ClientLastName");
        } else if (_.get(md, "ClientFirstName")) {
          clientName = _.get(md, "ClientFirstName");
        }

        return (
          <MyDrafts
            key={wantAdId}
            itemId={wantAdId}
            CompletedPercentage={CompletedPercentage}
            adType={adType}
            Current_Step={Current_Step}
            setDraftSelectedItem={handleDraftItemClick}
            latUpdatedDate={formattedDate}
            clientName={clientName}
            isFromWantAd={true}
            IsFilledByClient={IsFilledByClient}
            IsReadyToReview={IsReadyToReview}
          />
        );
      });

      return myDraftList;
    }
  };

  useEffect(() => {
    getProfileSlugApi();
  }, []);

  const getProfileSlugApi = async () => {
    await getProfileSlug()
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        setUserSlug(responseResult);
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  // Function to save want ad and generate custom link for buyer form
  const generateWantAdCustomLink = () => {
    // If slug is not ready, wait 2 seconds and try again.
    if (!userSlug) {
      setTimeout(() => {
        if (!userSlug) {
          alert(
            "Profile information is still loading. Please try again later.",
          );
        } else {
          processGenerateLink();
        }
      }, 2000);
      return;
    }

    processGenerateLink();
  };

  const processGenerateLink = () => {
    const requestPayLoad = {
      isFilledByClient: true,
      isAgentAsSeller: false,
      IsSavedDraft: true,
      IsPublished: false,
      cmpldStep: 1,
      cmpldPerctg: 7,
      adType: BUY,
      isAgentCreated: true,
    };

    saveMemberWantAd(requestPayLoad)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          setLinkToShare(
            process.env.REACT_APP_BUYERLISTING_SERVICE_BASE_URL +
              "/form/" +
              userSlug,
          );
          handleCustomLinkPopupOpen();
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  return (
    <>
      <CustomLinkModal
        show={showCustomLinkPopuop}
        onHide={handleCustomLinkPopupClose}
        linkToShare={linkToShare}
        isFromCreateWantAd={true}
      />

      <PremiumPlanModal
        userDetails={userDetailsFromStore[0]}
        show={showPremiumPlanModal}
        onHide={() => setPremiumPlanModal(false)}
        updateReloadParam={updateReloadParam}
        showPlanModal={() => setPremiumPlanModal(true)}
      />

      <div className="outer-wrap position-relative agent-no-login">
        <CommonHeader />
        <div className="m-0">
          <div className="left-sidebar p-0">{/* <Sidebar /> */}</div>
          <div className="main-content p-0 agent-no-login-content">
            {/* To do when admin login is true when we use (m-2) */}
            {/* <div className="row m-2"> */}
            <div className="row m-0">
              <div className="col-md-12">
                <div className="edit-want-ad-wrapper mw-640">
                  <h2 className="agent-create-head">
                    Create a Want-Listing
                  </h2>
                  <h6 className="mb-40 agent-create-para">
                    Select how you would you like to create your Want-Listing.{" "}
                  </h6>

                  <>
                    <div
                      className="create-new-wantad-wrap mb-3"
                      onClick={() => navigate("")}
                    >
                      <label className="radio-wrap">
                        <input
                          name="SELL4"
                          type="radio"
                          value="false"
                          onClick={() => {
                            if (isPremiumUser) generateWantAdCustomLink();
                            else setPremiumPlanModal(true);
                          }}
                        />
                        <span>
                          <div className="timing-wrap-info">
                            <h2 className="pb-0 timing-wrap-head">
                              Send a Buyer Listing Form to my client
                            </h2>
                            {!isPremiumUser && (
                              <img
                                src={PremiumFeatureIcon}
                                alt={PremiumFeatureIcon}
                                className=""
                              />
                            )}
                          </div>
                        </span>
                      </label>
                    </div>

                    <div className="text-center">
                      <div className="mt-3 mb-3">
                        <img src={BuyerList} className="wdImg" />
                      </div>
                      <div className="wdImg m-auto mb-3 boldTxt">
                        {" "}
                        <p className="wd-img-para">
                          After your buyer submits their criteria to you via
                          your custom link, we notify you to review and post it
                          from your drafts. (Buy only)
                        </p>
                      </div>
                    </div>
                  </>

                  <>
                    <SegmentDivider />
                    <div className="my-drafts-sec">
                      <div
                        className="create-new-wantad-wrap mt-4 mb-4"
                        onClick={() => navigate("/agent-create-want-ad")}
                      >
                        <label className="radio-wrap">
                          <input name="SELL4" type="radio" value="false" />
                          <span>
                            <div className="timing-wrap-info">
                              <h2 className="pb-0 timing-wrap-head">
                                {isPremiumUser
                                  ? "Fill it out myself"
                                  : "Create New Want-Listing"}{" "}
                              </h2>
                            </div>
                          </span>
                        </label>
                      </div>
                      {(myDrafts || isPremiumUser === true) && (
                        <>
                          <h2 className="mt-3">My Drafts</h2>
                          {loadDrafts()}
                        </>
                      )}
                    </div>
                  </>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PremiumPlanModal
        userDetails={userDetailsFromStore[0]}
        updateReloadParam={updateReloadParam}
        show={showPremiumPlanModal}
        onHide={PremiumPlanModalClose}
        showPlanModal={PremiumPlanModalShow}
      />
    </>
  );
};

export default AgentCreateOrEditWantAdDraftListing;
